import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { base64Decode } from '@app/helpers/base64url';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import DataTable, { TableStyles } from 'react-data-table-component';
import { Button } from '@app/components';
import {
  getMessagesByCentre,
  createMessage,
  updateMessage,
  activateMessage,
  deactivateMessage,
  deleteMessage
} from '@app/services/centreCustomMessage';
import moment from 'moment';

interface CustomMessage {
  id: number;
  centre_id: number;
  title: string;
  message_content: string;
  image: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  updated_by: number;
  created_by_name: string;
  updated_by_name: string;
}

interface LocationState {
  centre_id: number;
  centre_name: string;
}

const ManageCustomMessages = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { encodedId } = useParams<{ encodedId: string }>();

  let centre_id: number;
  let centre_name: string;

  if (encodedId) {
    try {
      const decoded = JSON.parse(base64Decode(encodedId));
      centre_id = decoded.centre_id;
      centre_name = decoded.centre_name;
    } catch {
      const state = (location.state as LocationState) || {} as LocationState;
      centre_id = state.centre_id;
      centre_name = state.centre_name;
    }
  } else {
    const state = (location.state as LocationState) || {} as LocationState;
    centre_id = state.centre_id;
    centre_name = state.centre_name;
  }
  const canAccess = useSelector((state: any) => state.auth.permissions?.centre);

  const [messages, setMessages] = useState<CustomMessage[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [existingImage, setExistingImage] = useState<string>('');

  useEffect(() => {
    // Check permission first
    if (!canAccess?.['custom-messages']) {
      toast.error('You do not have permission to access this page');
      navigate('/centers');
      return;
    }

    if (!centre_id) {
      toast.error('Centre ID is required');
      navigate('/centers');
      return;
    }
    fetchMessages();
  }, [centre_id, canAccess]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const resp = await getMessagesByCentre(centre_id);
      if (resp?.success === true) {
        setMessages(resp.collections);
      } else {
        toast.error(resp?.errors?.[0]?.msg || 'Failed to fetch messages');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Internal Server Error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setIsEditing(true);
    setEditingId(null);
    setTitle('');
    setMessageContent('');
    setImageFile(null);
    setImagePreview('');
    setExistingImage('');
  };

  const handleEdit = (row: CustomMessage) => {
    setIsEditing(true);
    setEditingId(row.id);
    setTitle(row.title || '');
    setMessageContent(row.message_content);
    setImageFile(null);
    setImagePreview('');
    setExistingImage(row.image || '');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingId(null);
    setTitle('');
    setMessageContent('');
    setImageFile(null);
    setImagePreview('');
    setExistingImage('');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please select a valid image file (JPG, PNG, GIF, WEBP)');
      return;
    }
    // Limit to 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setExistingImage('');
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title cannot be empty');
      return;
    }

    if (!messageContent.trim()) {
      toast.error('Message content cannot be empty');
      return;
    }

    try {
      setLoading(true);
      let resp;

      const formData = new FormData();
      formData.append('title', title.trim());
      formData.append('message_content', messageContent.trim());
      if (imageFile) {
        formData.append('image', imageFile);
      }

      if (editingId) {
        // Flag image removal when an existing image was cleared and no new one chosen
        if (!imageFile && !existingImage) {
          formData.append('remove_image', 'true');
        }
        // Update existing message
        resp = await updateMessage(centre_id, editingId, formData);
      } else {
        // Create new message
        resp = await createMessage(centre_id, formData);
      }

      if (resp?.success === true) {
        toast.success(resp.message || 'Message saved successfully');
        handleCancel();
        fetchMessages();
      } else {
        toast.error(resp?.errors?.[0]?.msg || 'Failed to save message');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Internal Server Error');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateConfirm = (row: CustomMessage) => {
    confirmAlert({
      title: 'Confirm Activation',
      message: 'Only one message can be active at a time. This will deactivate any currently active message. Continue?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => handleActivate(row.id)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const handleActivate = async (id: number) => {
    try {
      setLoading(true);
      const resp = await activateMessage(centre_id, id);
      if (resp?.success === true) {
        toast.success(resp.message || 'Message activated successfully');
        fetchMessages();
      } else {
        toast.error(resp?.errors?.[0]?.msg || 'Failed to activate message');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Internal Server Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateConfirm = (row: CustomMessage) => {
    confirmAlert({
      title: 'Confirm Deactivation',
      message: 'Are you sure you want to deactivate this message?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => handleDeactivate(row.id)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const handleDeactivate = async (id: number) => {
    try {
      setLoading(true);
      const resp = await deactivateMessage(centre_id, id);
      if (resp?.success === true) {
        toast.success(resp.message || 'Message deactivated successfully');
        fetchMessages();
      } else {
        toast.error(resp?.errors?.[0]?.msg || 'Failed to deactivate message');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Internal Server Error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = (row: CustomMessage) => {
    confirmAlert({
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this message? This action cannot be undone.',
      buttons: [
        {
          label: 'Yes',
          onClick: () => handleDelete(row.id)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  const handleDelete = async (id: number) => {
    try {
      setLoading(true);
      const resp = await deleteMessage(centre_id, id);
      if (resp?.success === true) {
        toast.success(resp.message || 'Message deleted successfully');
        fetchMessages();
      } else {
        toast.error(resp?.errors?.[0]?.msg || 'Failed to delete message');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Internal Server Error');
    } finally {
      setLoading(false);
    }
  };

  const customStyle: TableStyles = {
    head: {
      style: {
        color: '#5E5873',
        fontSize: '14px',
        fontWeight: 900,
      },
    },
    headCells: {
      style: {
        borderColor: '#E9ECEF',
        textTransform: 'uppercase',
        letterSpacing: '1px',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#F7F7F8',
        minHeight: '44px',
        fontWeight: 500,
      },
    },
    cells: {
      style: {
        fontSize: '14px',
      },
    },
  };

  const columns = [
    {
      name: 'Image',
      cell: (row: CustomMessage) =>
        row.image ? (
          <img
            src={row.image}
            alt={row.title || 'message image'}
            style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '4px', margin: '6px 0' }}
          />
        ) : (
          <span className="text-muted">---</span>
        ),
      width: '90px',
    },
    {
      name: 'Title',
      selector: (row: CustomMessage) => row.title || '---',
      cell: (row: CustomMessage) => (
        <div
          data-toggle="tooltip"
          title={row.title}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '180px',
            fontWeight: 600,
          }}
        >
          {row.title || '---'}
        </div>
      ),
      wrap: false,
      grow: 1,
    },
    {
      name: 'Message Content',
      selector: (row: CustomMessage) => row.message_content,
      cell: (row: CustomMessage) => (
        <div
          data-toggle="tooltip"
          title={row.message_content}
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '300px',
          }}
        >
          {row.message_content}
        </div>
      ),
      wrap: false,
      grow: 2,
    },
    {
      name: 'Status',
      selector: (row: CustomMessage) => row.is_active,
      cell: (row: CustomMessage) =>
        row.is_active ? (
          <span className="badge bg-success">Active</span>
        ) : (
          <span className="badge bg-secondary">Inactive</span>
        ),
      width: '120px',
    },
    {
      name: 'Created By',
      selector: (row: CustomMessage) => row.created_by_name || '---',
      wrap: true,
      width: '150px',
    },
    {
      name: 'Created At',
      selector: (row: CustomMessage) => row.created_at,
      cell: (row: CustomMessage) =>
        moment(row.created_at).format('DD MMM YYYY HH:mm'),
      width: '180px',
    },
    {
      name: 'Actions',
      cell: (row: CustomMessage) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {canAccess?.['custom-messages-edit'] && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleEdit(row);
              }}
              data-toggle="tooltip"
              title="Edit"
            >
              <i className="fas fa-pen" />
            </a>
          )}
          {canAccess?.['custom-messages-activate'] && (
            row.is_active ? (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleDeactivateConfirm(row);
                }}
                data-toggle="tooltip"
                title="Deactivate"
              >
                <i className="fas fa-toggle-on" style={{ color: '#28a745' }} />
              </a>
            ) : (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleActivateConfirm(row);
                }}
                data-toggle="tooltip"
                title="Activate"
              >
                <i className="fas fa-toggle-off" style={{ color: '#6c757d' }} />
              </a>
            )
          )}
          {canAccess?.['custom-messages-delete'] && (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm(row);
              }}
              data-toggle="tooltip"
              title="Delete"
            >
              <i className="fas fa-trash" />
            </a>
          )}
        </div>
      ),
      width: '150px',
    },
  ];

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>Manage Custom Messages - {centre_name || 'Centre'}</h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <a href="/">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/centers">Centres</Link>
                </li>
                <li className="breadcrumb-item active">Custom Messages</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Custom Messages</h3>
                  <div className="col-lg-12 text-right">
                    <Link to="/centers" className="btn btn-secondary mr-2">
                      <i className="fas fa-arrow-left mr-2" />
                      Back to List
                    </Link>
                    {!isEditing && canAccess?.['custom-messages-create'] && (
                      <Button
                        className="btn btn-primary"
                        onClick={handleAddNew}
                        disabled={isLoading}
                      >
                        + Add New Message
                      </Button>
                    )}
                  </div>
                </div>

                <div className="card-body">
                  {isEditing && (
                    <div className="row mb-4">
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header">
                            <h5 className="card-title">
                              {editingId ? 'Edit Message' : 'Add New Message'}
                            </h5>
                          </div>
                          <div className="card-body">
                            <div className="form-group">
                              <label htmlFor="title">
                                Title <span className="text-danger">*</span>
                              </label>
                              <input
                                id="title"
                                type="text"
                                className="form-control"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Enter title..."
                                disabled={isLoading}
                                maxLength={150}
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor="messageContent">
                                Message Content <span className="text-danger">*</span>
                              </label>
                              <textarea
                                id="messageContent"
                                className="form-control"
                                rows={4}
                                value={messageContent}
                                onChange={(e) => setMessageContent(e.target.value)}
                                placeholder="Enter message content..."
                                disabled={isLoading}
                              />
                            </div>
                            <div className="form-group">
                              <label htmlFor="messageImage">Image</label>
                              <input
                                id="messageImage"
                                type="file"
                                accept="image/*"
                                className="form-control"
                                onChange={handleImageChange}
                                disabled={isLoading}
                              />
                              <small className="text-muted">Accepted: JPG, PNG, GIF, WEBP. Max size 5MB.</small>
                              {(imagePreview || existingImage) && (
                                <div style={{ marginTop: '10px' }}>
                                  <img
                                    src={imagePreview || existingImage}
                                    alt="preview"
                                    style={{ maxWidth: '160px', maxHeight: '120px', objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: '4px', display: 'block' }}
                                  />
                                  <Button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger mt-2"
                                    onClick={handleRemoveImage}
                                    disabled={isLoading}
                                  >
                                    <i className="fas fa-times mr-1" /> Remove Image
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="form-group">
                              <Button
                                type="button"
                                className="btn btn-success mr-2"
                                onClick={handleSave}
                                isLoading={isLoading}
                              >
                                Save
                              </Button>
                              <Button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancel}
                                disabled={isLoading}
                              >
                                Cancel
                              </Button>
                            </div>
                            <div className="alert alert-info">
                              <i className="fas fa-info-circle mr-2" />
                              Note: Only one message can be active at a time. Activating a
                              message will automatically deactivate any other active message.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <DataTable
                    columns={columns}
                    data={messages}
                    progressPending={isLoading}
                    customStyles={customStyle}
                    noDataComponent="No custom messages found. Click 'Add New Message' to create one."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManageCustomMessages;
